import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { createVaultMember } from '../store/slices/vaultMemberSlice';
import { getUsers } from '../store/slices/userSlice';
import type { VaultMemberCreateDto } from '../types/api';
import type { UserItem } from '../store/slices/userSlice';

interface ShareVaultModalProps {
  isOpen: boolean;
  onClose: () => void;
  vaultId: string;
  vaultName: string;
  onSuccess?: () => void;
}

function userDisplayLabel(user: UserItem): string {
  if (user.name?.trim()) return user.email?.trim() ? `${user.name.trim()} (${user.email.trim()})` : user.name.trim();
  if (user.email?.trim()) return user.email.trim();
  return user.id;
}

const ShareVaultModal: React.FC<ShareVaultModalProps> = ({
  isOpen,
  onClose,
  vaultId,
  vaultName,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector((state) => state.auth.user?.id ?? null);
  const allUsers = useAppSelector((state) => state.user.items);
  const users = currentUserId ? allUsers.filter((u) => u.id !== currentUserId) : allUsers;
  const usersLoading = useAppSelector((state) => state.user.operationLoading.list);
  const [userId, setUserId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      dispatch(getUsers());
      setUserId('');
      setError(null);
    }
  }, [isOpen, dispatch]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isSubmitting) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedUserId = userId.trim();
    if (!trimmedUserId) {
      setError(t('pages.workspace.shareModal.userRequired'));
      return;
    }
    const selectedUser = users.find((u) => u.id === trimmedUserId);
    if (!selectedUser) {
      setError(t('pages.workspace.shareModal.userRequired'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const body: VaultMemberCreateDto = { vaultId, userName: userDisplayLabel(selectedUser) };
      const result = await dispatch(createVaultMember(body)).unwrap();
      if (result) {
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages.workspace.shareModal.inviteFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
        onClick={handleBackdropClick}
      />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 w-full max-w-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 dark:border-gray-700/80">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('pages.workspace.shareModal.title', { name: vaultName })}
            </h3>
          </div>
          <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {t('pages.workspace.shareModal.usernameLabel')}
              </label>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                disabled={usersLoading}
                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow disabled:opacity-60"
              >
                <option value="">{t('pages.workspace.shareModal.selectUser')}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {userDisplayLabel(user)}
                  </option>
                ))}
              </select>
            </div>
            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
            <div className="px-0 py-2 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2.5 text-sm font-medium rounded-xl bg-primary hover:bg-primary/90 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-1.5" />
                    {t('common.processing')}
                  </>
                ) : (
                  t('pages.workspace.shareModal.invite')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default ShareVaultModal;
